/**
 * Dice's one entry point, in two verbs ({@link RollEntry}): **load** a bonus into the viewer's roller
 * to add accuracy or difficulty (`openDice`), or **roll** it flat right now (`useFlatRoll`). DICE.app's
 * row dice and the PILOT and MECH sheets' roll buttons roll flat; COMBAT's stat cells load.
 *
 * The shell owns the loader and hands it to the stage's tenants (`StageControls.openDice`). A
 * player's roller is the DICE rail page (`decisions/0203` point 5); the GM has no DICE page
 * (`dice-rework` 1.2), so the GM's seed goes to the COMMS dice composer instead (`CommsDice`, through
 * `StageControls.commsDice`). Results post to COMMS either way.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_ROLL_LABEL_LENGTH } from "@lancer-vtt/shared";
import { useGameSessionContext } from "../../../lib/game/GameSessionProvider";
import type { PreselectBonus } from "../checkComposer";
import { composeCheckNotation, labelRoll } from "../checkNotation";
import type { RailPage } from "../railState";

/** A rollable stat's two verbs. `roll` is false when the post was refused (link down). */
export type RollEntry = {
  load: (preselect: PreselectBonus) => void;
  roll: (preselect: PreselectBonus) => boolean;
};

/** Roll a bonus flat, right now: `1d20 + bonus` with no accuracy or difficulty, posted as a `/roll` card named by its label. */
export function useFlatRoll(): (preselect: PreselectBonus) => boolean {
  const { postChat } = useGameSessionContext();
  return useCallback((p: PreselectBonus) => postChat(labelRoll(composeCheckNotation(p.bonus, 0, 0), p.label)), [postChat]);
}

/** A short request tag for {@link usePostedRoll}'s label: four base-36 characters. */
function mintRollTag(): string {
  return Math.floor(Math.random() * 36 ** 4).toString(36).toUpperCase().padStart(4, "0");
}

/** How long {@link usePostedRoll} waits for its card before giving up. */
const POSTED_ROLL_TIMEOUT_MS = 10_000;

type PendingRoll = { label: string; seen: ReadonlySet<string>; resolve: (total: number | null) => void };

/**
 * Roll a dice notation for a value the app keeps (the Engineer's Limited roll): the roll posts to
 * COMMS as a `/roll <notation> # <label> · <tag>` card the whole table sees, and the promise
 * resolves with the server's total off that card. The tag is minted per call, so the card matched
 * is this request's own, never another roll that lands in between (the same label from another tab
 * of this slot, a second press). `null` when the post was refused (link down) or no card came back
 * in time.
 */
export function usePostedRoll(): (notation: string, label: string) => Promise<number | null> {
  const { postChat, feed, actorId } = useGameSessionContext();
  const pending = useRef<PendingRoll[]>([]);
  const feedRef = useRef(feed);
  feedRef.current = feed;
  useEffect(() => {
    if (pending.current.length === 0) return;
    pending.current = pending.current.filter((p) => {
      const card = feed.find((e) => e.kind === "roll" && !p.seen.has(e.id) && e.authorSlotId === actorId && e.label === p.label);
      if (card?.kind !== "roll") return true;
      p.resolve(card.total);
      return false;
    });
  }, [feed, actorId]);
  return useCallback(
    (notation: string, label: string) => {
      // The server trims and caps the label, so the tag goes on after the cap, where it survives it.
      const tag = ` · ${mintRollTag()}`;
      const name = `${label.trim().slice(0, MAX_ROLL_LABEL_LENGTH - tag.length).trim()}${tag}`;
      const seen = new Set(feedRef.current.map((e) => e.id));
      return new Promise<number | null>((resolve) => {
        if (!postChat(labelRoll(`/roll ${notation}`, name))) return resolve(null);
        const entry: PendingRoll = { label: name, seen, resolve };
        pending.current.push(entry);
        setTimeout(() => {
          if (!pending.current.includes(entry)) return;
          pending.current = pending.current.filter((p) => p !== entry);
          resolve(null);
        }, POSTED_ROLL_TIMEOUT_MS);
      });
    },
    [postChat],
  );
}

export type RailDice = {
  /** The bonus DICE is seeded with, or `null` for "opened bare". */
  preselect: PreselectBonus | null;
  /** Open the DICE page, re-seeding `preselect` every call (null when called bare). */
  openDice: (preselect?: PreselectBonus) => void;
};

/**
 * `DiceApp`'s re-seed effect keys on the `preselect` prop's reference identity, so every caller hands
 * in a fresh object and this stores it as-is: pressing the same cell twice re-seeds. Don't memoize a
 * preselect on the way in.
 */
export function useRailDice(open: (page: RailPage) => unknown, role: "gm" | "player" = "player"): RailDice {
  const [preselect, setPreselect] = useState<PreselectBonus | null>(null);
  const openDice = useCallback(
    (next?: PreselectBonus) => {
      setPreselect(next ?? null);
      if (role === "player") void open({ app: "dice" });
    },
    [open, role],
  );
  return { preselect, openDice };
}
